import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { Prose } from '@/components/layout/Prose';
import { fadeUp, fadeIn, staggerContainer, STAGGER } from '@/lib/motion';

export type LegalSlug = 'privacy' | 'terms' | 'dpa';

interface LegalSubsection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export function LegalContent({ slug }: { slug: LegalSlug }) {
  const { t } = useTranslation('legal');
  const sections = t(`${slug}.sections`, { returnObjects: true }) as readonly LegalSubsection[];

  return (
    <>
      <Section padding="lg">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fadeIn}
              className="mb-4 text-xs font-semibold uppercase tracking-widest text-brand"
            >
              {t(`${slug}.eyebrow`)}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t(`${slug}.title`)}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              {t(`${slug}.subtitle`)}
            </motion.p>
            <motion.p variants={fadeUp} className="mt-4 text-sm text-muted-foreground">
              {t('common.last_updated_label', { date: t(`${slug}.last_updated`) })}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section padding="lg" bordered>
        <Container size="md">
          <Prose className="mx-auto">
            {sections.map((section, idx) => (
              <section key={idx}>
                <h2>{section.heading}</h2>
                {section.paragraphs?.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
                {section.bullets && section.bullets.length > 0 && (
                  <ul>
                    {section.bullets.map((bullet, i) => (
                      <li key={i}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </Prose>
        </Container>
      </Section>
    </>
  );
}
